import { Pagination, Stack, TableCell, TableRow } from "@mui/material";
import { TextLink, VSpacer } from "@/components/DesignSystem";
import React, { useState } from "react";
import { useQuery } from "react-query";
import { QueryKeys } from "@/constants/QueryKeys";
import { IntegrationApi } from "@/utilities/api/IntegrationApi";
import { IntegrationSyncStatus, IntegrationSyncType } from "@shared/enums";
import { ProcessingStatus } from "@shared/enums/ProcessingStatus";
import {
  handleDownloadErrorCsv,
} from "@/pages/Admin/HierarchyOfRetailers/Integrations/IntegrationDetails";
import {
  ProcessingStatusChip,
} from "@/pages/Admin/HierarchyOfRetailers/ProcessingStatusChip";
import { ApiIntegrationSync } from "@api/interfaces";
import { Table } from "@/components/DesignSystem/Table/Table";
import { DateTime } from "luxon";

const AbandonedSyncStatuses: IntegrationSyncStatus[] = [
  IntegrationSyncStatus.Failed,
  IntegrationSyncStatus.Timeout,
];

interface SyncHistoryTableProps {
  retailerId: string,
  selectedSyncType: IntegrationSyncType,
}

export const SyncHistoryTable = ({
  retailerId,
  selectedSyncType,
}: SyncHistoryTableProps) => {
  const [page, setPage] = useState(0);

  const { data: syncHistoryResponse } = useQuery(
    [QueryKeys.GET_INTEGRATION_SYNC_HISTORY, retailerId, selectedSyncType, page],
    async () => IntegrationApi.getSyncHistory(
      retailerId,
      { limit: 20, page, syncType: selectedSyncType },
    ),
  );

  const getProcessingStatusCell = (syncHistory: ApiIntegrationSync) => {
    const earnsPoints = selectedSyncType === IntegrationSyncType.Transactions;
    const abandonedBeforeProcessing = AbandonedSyncStatuses.includes(syncHistory.syncStatus)
      && syncHistory.processingStatus === ProcessingStatus.Unprocessed;
    const hasProcessingJob = earnsPoints && !abandonedBeforeProcessing;
    return hasProcessingJob ? (
      <ProcessingStatusChip
        processingStatus={syncHistory.processingStatus}
        testID={`${syncHistory.id}-processing-status-chip`}
      />
    ) : '—';
  };

  return (
    <>
      <Table headers={['Date', 'Sync status', 'Processing status', 'Errors']}>
        {!!syncHistoryResponse?.data && syncHistoryResponse.data.map(syncHistory => (
          <TableRow key={syncHistory.id}>
            <TableCell>
              {
                DateTime
                  .fromJSDate(syncHistory.syncDate, { zone: 'America/New_York' })
                  .toFormat('MMM dd, yyyy')
              }
            </TableCell>
            <TableCell>{syncHistory.syncStatus}</TableCell>
            <TableCell>{getProcessingStatusCell(syncHistory)}</TableCell>
            <TableCell>
              {!!syncHistory.errors?.length && (
                <TextLink
                  onClick={() => {
                    handleDownloadErrorCsv(selectedSyncType, syncHistory.errors!);
                  }}
                  testID={`download-${selectedSyncType}-errors-button`}
                >
                  Download errors
                </TextLink>
              )}
            </TableCell>
          </TableRow>
        ))}
      </Table>
      <VSpacer size="3" />
      {!!syncHistoryResponse && !!syncHistoryResponse.lastPage && (
        <Stack
          alignItems='center'
          direction='row'
          justifyContent='center'
        >
          <Pagination
            count={syncHistoryResponse.lastPage + 1}
            onChange={(_, page) => {
              setPage(page - 1);
            }}
            page={syncHistoryResponse.page + 1}
          />
        </Stack>
      )}
    </>
  );
};